import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import apiRoutes from "../constants/api-routes";
import {
	findPluginMetaInPlatformsPayload,
	pickPluginDisplayNameFromPayload,
} from "../helpers/pluginNavHelpers";
import { selectOrganizationId } from "../redux/userSlice";
import useAuthHeader from "./useAuthHeader";

/**
 * Resolves a human-readable plugin name the same way as `SidebarNavigation`:
 * detail GET first, then `organizations/.../platforms-and-plugins` when the detail payload omits `name`.
 */
function useResolvedPluginDisplayName(
	pluginId: string,
	pluginPayload: unknown | null | undefined,
): { displayName: string; isResolvingName: boolean } {
	const organizationId = useSelector(selectOrganizationId);
	const authHeader = useAuthHeader();

	const nameFromDetail = useMemo(
		() => (pluginPayload ? pickPluginDisplayNameFromPayload(pluginPayload) : null),
		[pluginPayload],
	);

	const [nameFromPlatforms, setNameFromPlatforms] = useState<string | null>(null);
	const [platformsLookupDone, setPlatformsLookupDone] = useState(false);

	useEffect(() => {
		if (!pluginPayload || nameFromDetail) {
			setNameFromPlatforms(null);
			setPlatformsLookupDone(true);
			return;
		}
		if (organizationId == null) {
			setNameFromPlatforms(null);
			setPlatformsLookupDone(true);
			return;
		}

		let cancelled = false;
		setPlatformsLookupDone(false);
		axios
			.get(apiRoutes.getPlatformsAndPlugins(String(organizationId)), { headers: authHeader })
			.then((res) => {
				if (cancelled) return;
				const meta = findPluginMetaInPlatformsPayload(res.data, pluginId);
				setNameFromPlatforms(meta.name);
			})
			.catch(() => {
				if (!cancelled) setNameFromPlatforms(null);
			})
			.finally(() => {
				if (!cancelled) setPlatformsLookupDone(true);
			});

		return () => {
			cancelled = true;
		};
	}, [pluginPayload, pluginId, nameFromDetail, organizationId, authHeader]);

	const displayName =
		nameFromDetail ?? nameFromPlatforms ?? `Plugin ${pluginId}`;

	const isResolvingName =
		Boolean(pluginPayload)
		&& !nameFromDetail
		&& organizationId != null
		&& !platformsLookupDone;

	return { displayName, isResolvingName };
}

export default useResolvedPluginDisplayName;
